import { axiosAzureInstance } from '../Utils/axios';
import FileDownload from 'js-file-download';
import Notification from '../Components/Layout/Notification';
import useSWR from 'swr';
import { createCsv } from '../Utils/fileUtil';
import type { AxiosResponse } from 'axios';

const API_ENDPOINTS = {
  USERS: '/uaa/api/users',
  TENANTS: '/uaa/api/tenants',
  AUTHORITIES: '/uaa/api/authorities',
} as const;

const ENTITY_TYPES = {
  USER: 'user',
  TENANT: 'tenant',
  ROLE: 'role',
} as const;

type EntityType = (typeof ENTITY_TYPES)[keyof typeof ENTITY_TYPES];

interface FetcherParams {
  sort?: string;
  order?: string;
  [key: string]: string | number | boolean | undefined;
}

const fetcher = async (url: string, params: FetcherParams = {}): Promise<unknown> => {
  const defaultParams: FetcherParams = { sort: 'name', order: 'asc' };
  const combinedParams = { ...defaultParams, ...params };
  const response = await axiosAzureInstance.get(url, { params: combinedParams });
  return response.data;
};

const fetchData = async (
  url: string,
  params?: Record<string, string | number | boolean | undefined>
): Promise<AxiosResponse> => {
  return await axiosAzureInstance.get(url, { params });
};

const handleApiError = (error: Error): void => {
  Notification.showError(
    `Unexpected error occurred (description: ${error.message}). Please try again later.`
  );
};

const exportData = async (
  url: string,
  fileNamePrefix: string,
  entityType: EntityType,
  params: Record<string, string | number | boolean | undefined>
): Promise<void> => {
  try {
    const response = await fetchData(url, params);
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString().replace(/\//g, '-');
    const fileName = `${fileNamePrefix}_export_${formattedDate}.csv`;
    let file: Blob | undefined;

    if (ENTITY_TYPES.USER === entityType) {
      file = await createCsv(response.data.content, entityType);
    } else {
      file = await createCsv(response.data, entityType);
    }

    if (file) {
      FileDownload(file, fileName);
    }
  } catch (error) {
    handleApiError(error as Error);
  }
};

const createApiFunction =
  (method: 'post' | 'put', url: string) =>
  async (data: unknown): Promise<unknown> => {
    const response = await axiosAzureInstance[method](url, data);
    return response.data;
  };

const deleteApiFunction =
  (url: string) =>
  async (id: string | number): Promise<void> => {
    await axiosAzureInstance.delete(`${url}/${id}`);
  };

// Users
export const createUser = createApiFunction('post', API_ENDPOINTS.USERS);
export const updateUser = createApiFunction('put', API_ENDPOINTS.USERS);
export const deleteUser = deleteApiFunction(API_ENDPOINTS.USERS);

// Tenants
export const createTenant = createApiFunction('post', API_ENDPOINTS.TENANTS);
export const updateTenant = createApiFunction('put', API_ENDPOINTS.TENANTS);
export const deleteTenant = deleteApiFunction(API_ENDPOINTS.TENANTS);

// Roles
export const createRole = createApiFunction('post', API_ENDPOINTS.AUTHORITIES);
export const updateRole = createApiFunction('put', API_ENDPOINTS.AUTHORITIES);
export const deleteRole = deleteApiFunction(API_ENDPOINTS.AUTHORITIES);

interface UsersParams {
  page?: number;
  size?: number;
  sort?: string;
  order?: string;
  [key: string]: string | number | boolean | undefined;
}

interface TenantsParams {
  page?: number;
  size?: number;
  sort?: string;
  name?: string;
  tenantKey?: string;
}

interface RolesParams {
  page?: number;
  size?: number;
  sort?: string;
  name?: string;
}

export const getUsers = async ({
  page,
  size,
  sort,
  order,
  ...filters
}: UsersParams): Promise<AxiosResponse> => {
  return fetchData(API_ENDPOINTS.USERS, { page, size, sort, order, ...filters });
};

export const getUsersByTenantKey = async (tenantKey: string): Promise<AxiosResponse> => {
  return fetchData(`${API_ENDPOINTS.USERS}/tenant/${tenantKey}`);
};

export const getUsersByRoleId = async (roleId: string | number): Promise<AxiosResponse> => {
  return fetchData(`${API_ENDPOINTS.USERS}/role/${roleId}`);
};

export const getTenants = async ({
  page,
  size,
  sort,
  name,
  tenantKey,
}: TenantsParams): Promise<AxiosResponse> => {
  return fetchData(API_ENDPOINTS.TENANTS, { page, size, sort, name, tenantKey });
};

export const getRoles = async ({ page, size, sort, name }: RolesParams): Promise<AxiosResponse> => {
  return fetchData(API_ENDPOINTS.AUTHORITIES, { page, size, sort, name });
};

export const isRoleRemovable = async (roleId: string | number): Promise<AxiosResponse> => {
  return await axiosAzureInstance.get(`${API_ENDPOINTS.AUTHORITIES}/${roleId}/isRemovable`);
};

export const exportUsers = async ({ sort, order, ...filters }: UsersParams): Promise<void> => {
  await exportData(API_ENDPOINTS.USERS, 'users', ENTITY_TYPES.USER, {
    sort,
    order,
    ...filters,
    export: true,
  });
};

export const exportTenants = async ({ sort, name, tenantKey }: TenantsParams): Promise<void> => {
  await exportData(API_ENDPOINTS.TENANTS, 'tenants', ENTITY_TYPES.TENANT, {
    sort,
    name,
    tenantKey,
    export: true,
  });
};

export const exportRoles = async ({ sort, name }: RolesParams): Promise<void> => {
  await exportData(API_ENDPOINTS.AUTHORITIES, 'user_roles', ENTITY_TYPES.ROLE, {
    sort,
    name,
    export: true,
  });
};

export const useTenants = () => {
  const { data, error, mutate } = useSWR(`${API_ENDPOINTS.TENANTS}/all`, fetcher);
  return {
    tenants: (data as unknown[]) || [],
    loadingTenants: !data && !error,
    error,
    mutate,
  };
};

export const useRoles = () => {
  const { data, error, mutate } = useSWR(`${API_ENDPOINTS.AUTHORITIES}/all`, fetcher);
  return {
    roles: (data as unknown[]) || [],
    loadingRoles: !data && !error,
    errorTenants: error,
    mutateTenants: mutate,
  };
};

export const getLockDuration = async (login: string): Promise<unknown> => {
  const response = await axiosAzureInstance.get(`${API_ENDPOINTS.USERS}/${login}/lock-duration`);
  return response.data;
};

export const unlockUser = async (login: string): Promise<unknown> => {
  const response = await axiosAzureInstance.put(`${API_ENDPOINTS.USERS}/${login}/unlock`);
  return response.data;
};

const UserManagementService = {
  getUsers,
  getUsersByTenantKey,
  getUsersByRoleId,
  createUser,
  updateUser,
  deleteUser,
  exportUsers,
  getTenants,
  createTenant,
  updateTenant,
  deleteTenant,
  useTenants,
  exportTenants,
  getRoles,
  createRole,
  updateRole,
  isRoleRemovable,
  deleteRole,
  useRoles,
  exportRoles,
  getLockDuration,
  unlockUser,
};

export default UserManagementService;
